import { createContext, useContext } from 'react';
import type { GLTF } from 'three-stdlib';
import type {
  ManualFeatureInput,
  ManualStoredFeature,
} from '@/services/manualCad';

// Live client-side mirror of the active Manual-CAD (FreeCAD-style) session:
// the feature list + the tessellated Tip mesh (as a GLTF for the viewer). The
// server holds the source of truth; this context caches the last response and
// drives the feature bar, the sidebar tree, and the viewport. See
// docs/freecad-manual-cad-integration-plan.md §4.5.

export interface ManualCadContextValue {
  sessionId: string | null;
  features: ManualStoredFeature[];
  tipId: string | null;
  /** Tip mesh for the viewer, or null when there's no solid yet. */
  tipGltf: GLTF | null;
  /** A round-trip is in flight (disable inputs). */
  busy: boolean;
  /** Last recompute/geometry error, if any (e.g. "Boolean not available in v1"). */
  error: string | null;
  /** Append a feature; lazily creates the session on first call. */
  addFeature: (feature: ManualFeatureInput) => Promise<void>;
  /** Delete a feature by id and recompute. */
  deleteFeature: (featureId: string) => Promise<void>;
  /** Discard the session (e.g. leaving Manual mode). */
  reset: () => void;
}

export const ManualCadContext = createContext<ManualCadContextValue | null>(
  null,
);

export function useManualCad() {
  const ctx = useContext(ManualCadContext);
  if (!ctx)
    throw new Error('useManualCad must be used within ManualCadProvider');
  return ctx;
}

/** Non-throwing variant for components that may render outside the provider. */
export function useManualCadOptional() {
  return useContext(ManualCadContext);
}
